import React, { useState, useEffect } from 'react';
import { Link } from 'gatsby';
import { navigate } from 'gatsby';
import { AppBar, Toolbar, Button, ButtonGroup, Box, IconButton, Slide, Modal, useMediaQuery } from '@mui/material';
import { MobileMenu } from './mobileMenu';
import { useHeaderContent } from './queries/useHeaderContent';
import { styles } from './styles';
import { HeaderProps, ContentReference } from '../../../interfaces/HeaderMobile';
import GlobalContext from '../../../contexts/globalContext';
import Login from '../../atoms/login/login';
import ModalGuiaCdc from '../modal-guia-cdc/modalGuiaCdc';
import ZonaTransaccional from '../../molecules/zona-transaccional/zonaTransaccional';
import Searcher from '../../molecules/searcher';

import './header.scss';

const Header: React.FC<HeaderProps> = ({
  isDark,
  isMenuActive,
  isSearcherActive,
  setMenuActive,
  setSearcherActive
}) => {
  const [active, setActive] = useState<string>('Personas');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [referentContent, setReferentContent] = useState<ContentReference[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMobile = useMediaQuery('(max-width:1280px)');
  const headerContent = useHeaderContent();
  const [activeSubMenu, setActiveSubMenu] = useState<ContentReference | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { logoMagenta, logoBlack, menuOpciones, opciones, logosHeader } = headerContent;

  useEffect(() => {
    const pathSegments = window.location.pathname
      .toLowerCase()
      .split('/')
      .filter(segment => segment);
    if (pathSegments.includes('empresas')) {
      setActive('Empresas');
    } else {
      setActive('Personas');
    }
  }, []);

  useEffect(() => {
    if (!opciones) return;

    const validSwitchSelect = active === 'Personas' || active === 'Empresas' ? active : 'Personas';
    const foundOption = opciones.find(option => option.tituloBanner === validSwitchSelect);

    if (foundOption?.referenciaContenido) {
      setReferentContent(foundOption.referenciaContenido);
    }
  }, [active, opciones]);

  useEffect(() => {
    const handleClickOutside = event => {
      // Añadir esta comprobación para detectar clics en elementos del submenú
      const isMenuLink = event.target.closest('.m-header__dropdown-link');

      if (
        activeSubMenu &&
        !event.target.closest('.DropdownSection') &&
        !event.target.closest(`#dropdown-button-${referentContent.indexOf(activeSubMenu)}`) &&
        !isMenuLink // No cerrar el menú si se hizo clic en un elemento del submenú
      ) {
        handleMenuClose();
      }
    };

    if (activeSubMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeSubMenu, referentContent]);

  useEffect(() => {
    const handleKeyDown = event => {
      if (event.key === 'Escape' && activeSubMenu) {
        handleMenuClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeSubMenu]);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const handleSearcher = () => {
    setSearcherActive(true);
  };

  const handleClick = item => {
    setActive(item.nombreTag);
    navigate(item.slugSubcategorias, {
      state: { activeTag: item.nombreTag },
      replace: true
    });
    setAnchorEl(item.currentTarget);
  };

  const constructUrl = (targetPath = '') => {
    const base = window.location.origin;
    const [firstSegment] = window.location.pathname.split('/').filter(Boolean);
    const cleanedPath = targetPath.startsWith('/') ? targetPath.slice(1) : targetPath;
    const finalPath = cleanedPath.startsWith(firstSegment + '/') 
      ? cleanedPath 
      : `${firstSegment}/${cleanedPath}`;
  
    return `${base}/${finalPath}`;
  };
  

  const closeModal = () => {
    document.body.style.overflow = 'scroll';
    setIsOpenModal(false);
  };

  const renderLoginButton = (gigyaContext: any) => (
    <Login
      isMenuActive={isMenuActive}
      title={headerContent.inicioSesion}
      login={gigyaContext.loginComfamaId}
      loginComfamaId={gigyaContext.loginComfamaId}
      logout={gigyaContext.logout}
      updateProfile={gigyaContext.updateProfile}
      gigyaAccount={gigyaContext.gigyaAccount}
      isOpenModal={isOpenModal}
      setIsOpenModal={setIsOpenModal}
    />
  );

  const handleMenuOpen = (event, item) => {
    if (activeSubMenu === item) {
      setActiveSubMenu(null);
      setAnchorEl(null);
      setIsMenuOpen(false);
    } else {
      if (activeSubMenu && activeSubMenu !== item) {
        handleMenuClose();
      }
      setAnchorEl(event.currentTarget);
      setActiveSubMenu(item);
      setIsMenuOpen(true);
    }
  };

  const handleMenuClose = () => {
    setActiveSubMenu(null);
    setAnchorEl(null);
    setIsMenuOpen(false);
  };

  useEffect(() => {
    const mainContent = document.getElementById('main-content');
    if (mobileOpen && mainContent) {
      mainContent.setAttribute('aria-hidden', 'true');
    } else if (mainContent) {
      mainContent.removeAttribute('aria-hidden');
    }
  }, [mobileOpen]);

  const handleCloseSearcher = () => {
    setSearcherActive(false);
  };

  return (
    <div className="m-header">
      <GlobalContext.Consumer>
        {data => (
          <div>
            <AppBar position="static" sx={styles.appBar}>
              {/* Primera barra: Logo y Menú */}
              <Toolbar
                sx={{
                  ...styles.toolbar(isMobile),
                  height: isMobile ? '60px !important' : '90px !important',
                  minHeight: isMobile ? '60px !important' : '90px !important',
                  maxHeight: isMobile ? '60px !important' : '90px !important',
                  px: '16px',
                  pr: '0px',
                  '@media (min-width:1280px)': {
                    px: '41px',
                  },
                  '@media (min-width:1440px)': {
                    px: '120px',
                  },
                  '@media (min-width:1920px)': {
                    px: '192px',
                  },
                }}
                
              >
                {/* Logo */}
                <Link
                  to="/"
                  style={{
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <img
                    src={isDark ? logoBlack.file.url : logoMagenta.file.url}
                    alt="Logo Servicio de Empleo Comfama"
                    className="m-header__logo"
                  />
                </Link>
                {isMobile && (
                  <>
                    {/* Botón del menú móvil (primero en el DOM) */}
                    <Button
                      onClick={handleDrawerToggle}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        minWidth: 'unset',
                        width: '104px',
                        height: '60px',
                        p: 0,
                        backgroundColor: '#EE2B7B',
                        borderRadius: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '4px',
                        '&:hover': {
                          backgroundColor: '#ee2b7b'
                        }
                      }}
                    >
                      {/* Icono ☰ / × */}
                      <Box
                        sx={{
                          fontSize: '24px',
                          fontWeight: 'normal',
                          lineHeight: 1,
                          height: '24px',
                          display: 'flex',
                          color: 'white',
                          alignItems: 'center'
                        }}
                      >
                        {mobileOpen ? '×' : '☰'}
                      </Box>

                      {/* Texto Menú */}
                      <Box
                        sx={{
                          fontSize: '12px',
                          color: 'white',
                          textTransform: 'none',
                          fontWeight: 'normal',
                          lineHeight: 1
                        }}
                      >
                        Menú
                      </Box>
                    </Button>
                  </>
                )}

                {!isMobile && (
                  <>
                    <nav className="m-header__nav">
                      <ButtonGroup disableElevation sx={styles.buttonGroup}>
                        {menuOpciones.map((item, index) => (
                          <Button
                            key={index}
                            onClick={() => handleClick(item)}
                            sx={styles.menuButton(active === item.nombreTag)}
                            aria-label={
                              item.nombreTag === 'Personas'
                                ? 'Botón servicios de empleo para personas'
                                : item.nombreTag === 'Empresas'
                                ? 'Botón servicios de empleo para empresas'
                                : item.nombreTag
                            }
                          >
                            {item.nombreTag}
                          </Button>
                        ))}
                      </ButtonGroup>
                    </nav>

                    <div className="m-header__menu">
                      <div className="m-header__actions">
                        <ZonaTransaccional />
                        <a
                          href="https://www.comfama.com/ayuda"
                          aria-label="boton ayuda"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="m-header__help"
                        >
                          Ayuda
                          <i className="icon-question-circle1" />
                        </a>
                        <button
                          type="button"
                          aria-label="boton buscador"
                          onClick={handleSearcher}
                          className="m-header__search gtmMenuTransBuscar"
                        >
                          <span>Buscador</span>
                          <i className="icon-search-header icon-search" />
                        </button>
                        {renderLoginButton(data.gigyaContext)}
                      </div>
                    </div>
                  </>
                )}
              </Toolbar>
            </AppBar>

            {/* SubMenu Bar (solo desktop) */}
            {!isMobile && referentContent.length > 0 && (
              <AppBar
                position="relative"
                sx={{
                  boxShadow: 'none',
                  backgroundColor: '#f8f8f8',
                  borderBottom: '1px solid #e0e0e0'
                }}
              >
                <Toolbar
                  sx={{
                    justifyContent: 'center',
                    minHeight: '0px !important'
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 6 }}>
                    {referentContent.map((item, index) => (
                      <div key={index} style={{ position: 'relative' }}>
                        <Button
                          id={`dropdown-button-${index}`}
                          aria-controls={Boolean(anchorEl && activeSubMenu === item) ? `dropdown-${index}` : undefined}
                          aria-expanded={Boolean(anchorEl && activeSubMenu === item)}
                          aria-haspopup="listbox"
                          onClick={e => {
                            if (Array.isArray(item.referenciaContenido) && item.referenciaContenido.length > 0) {
                              handleMenuOpen(e, item);
                            } else if (item.redireccionBoton) {
                              setActiveSubMenu(null);

                              const url = item.redireccionBoton.startsWith('/')
                                ? item.redireccionBoton
                                : `/${item.redireccionBoton}`;

                              navigate(url, {
                                replace: true
                              });
                            }
                          }}
                          endIcon={
                            item.referenciaContenido && item.referenciaContenido.length > 0 ? (
                              <i
                                className={`icon-chevron ${
                                  isMenuOpen && activeSubMenu === item ? 'icon-chevron-up' : 'icon-chevron-down'
                                }`}
                                style={{
                                  fontSize: '.8rem',
                                  fontWeight: 'normal !important',
                                  marginLeft: '8px'
                                }}
                              />
                            ) : null
                          }
                          sx={{
                            fontSize: '16px',
                            fontWeight: activeSubMenu === item ? 500 : 400,
                            color: activeSubMenu === item ? '#EE2B7B' : '#000',
                            textTransform: 'none',
                            padding: '10px 0',
                            transition: 'all 0.3s ease',
                            borderRadius: '0px',
                            display: 'flex',
                            alignItems: 'center',
                            borderBottom: activeSubMenu === item ? '2px solid #EE2B7B' : '2px solid transparent',
                            '&:hover': {
                              color: '#EE2B7B',
                              backgroundColor: 'transparent',
                              borderBottom: '2px solid #EE2B7B'
                            }
                          }}
                        >
                          {item.tipoTitulo}
                        </Button>

                        {item.referenciaContenido && item.referenciaContenido.length > 0 && activeSubMenu === item && (
                          <div
                            style={{
                              position: 'fixed',
                              zIndex: 9999,
                              background: 'white',
                              boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.1)',
                              borderRadius: '12px',
                              width: '380px',
                              padding: '16px',
                              marginTop: '8px',
                              top: `${anchorEl ? anchorEl.getBoundingClientRect().bottom : 0}px`,
                              left: `${
                                anchorEl ? anchorEl.getBoundingClientRect().left - 190 + anchorEl.offsetWidth / 2 : 0
                              }px`
                            }}
                            className="m-header__DropdownSection"
                          >
                            {item.referenciaContenido.map((submenu, subIndex) => (
                              <Link
                                key={subIndex}
                                to={submenu.redireccionBoton}
                                onClick={e => {
                                  e.preventDefault();

                                  // Lógica de redirección
                                  const currentUrl = window.location.href;
                                  const currentUrlObj = new URL(currentUrl);
                                  const pathSegments = currentUrlObj.pathname.split('/').filter(segment => segment);
                                  const baseSegment = pathSegments.length > 0 ? pathSegments[0] : '';

                                  let redirectUrl = submenu.redireccionBoton;
                                  redirectUrl = redirectUrl.replace(/^\/+/, '/');

                                  try {
                                    new URL(redirectUrl);
                                    window.location.href = redirectUrl;
                                  } catch (e) {
                                    const origin = currentUrlObj.origin;

                                    const normalizePath = (...parts) =>
                                      parts
                                        .map(part => part.replace(/^\/+|\/+$/g, ''))
                                        .filter(Boolean)
                                        .join('/');

                                    if (baseSegment && redirectUrl.includes(baseSegment)) {
                                      const parts = redirectUrl.split(baseSegment);
                                      const uniquePath = parts[parts.length - 1] || '';

                                      window.location.href = `${origin}/${normalizePath(baseSegment, uniquePath)}`;
                                    } else {
                                      window.location.href = `${origin}/${normalizePath(baseSegment, redirectUrl)}`;
                                    }
                                  }

                                  handleMenuClose();
                                }}
                                style={{
                                  textDecoration: 'none',
                                  display: 'block',
                                  color: 'inherit'
                                }}
                                className="m-header__dropdown-link"
                              >
                                <div className="m-header__DropdownItemOpción">{submenu.tituloBanner}</div>
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </Box>

                  {active === 'Empresas' && (
                    <Button
                      variant="outlined"
                      href={constructUrl(opciones[1].redireccionBoton)}
                      sx={{
                        color: '#EE2B7B',
                        borderColor: '#EE2B7B',
                        borderRadius: '20px',
                        textTransform: 'none',
                        padding: '6px 16px',
                        marginLeft: '48px',
                        height: '30px',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {opciones[1].tituloBoton}
                    </Button>
                  )}
                </Toolbar>
              </AppBar>
            )}

            {/* Modal */}
            {isOpenModal && <ModalGuiaCdc showModal={isOpenModal} onClose={closeModal} />}

            {isMobile && (
              <>
                {/* Mobile Toolbar with search and login (segundo y tercero en orden DOM) */}
                <Toolbar
                  aria-hidden={mobileOpen}
                  sx={{
                    borderTop: '1px solid #e0e0e0',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    width: '100%',
                    gap: 2,
                    padding: '8px 16px',
                    minHeight: '48px !important',
                    visibility: mobileOpen ? 'hidden' : 'visible'
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      marginLeft: 'auto'
                    }}
                  >
                    {/* Search (segundo) */}
                    <IconButton
                      onClick={handleSearcher}
                      sx={{
                        color: '#333',
                        padding: '8px'
                      }}
                      aria-label="Buscar"
                    >
                      <i className="icon-search" style={{ fontSize: '20px' }} />
                    </IconButton>

                    <Box
                      sx={{
                        borderLeft: '1px solid #e0e0e0',
                        height: '24px',
                        margin: '0 8px'
                      }}
                    />

                    {/* Login/Avatar (tercero) */}
                    {renderLoginButton(data.gigyaContext)}
                  </Box>
                </Toolbar>

                {/* Mobile Menu modal */}
                {mobileOpen && (
                <Modal
                  open={mobileOpen}
                  onClose={handleDrawerToggle}
                  aria-labelledby="mobile-menu-title"
                  aria-modal="true"
                >
                  <Slide direction="left" in={mobileOpen} mountOnEnter unmountOnExit>
                    <Box
                      sx={{
                        position: 'fixed',
                        top: 0,
                        right: 0,
                        width: '100%',
                        height: '100vh',
                        bgcolor: 'white',
                        zIndex: 1300,
                        outline: 'none',
                        overflow: 'auto',
                        boxSizing: 'border-box',
                        boxShadow: '-4px 0 10px rgba(0,0,0,0.1)'
                      }}
                      role="dialog"
                      aria-labelledby="mobile-menu-title"
                    >
                      <div>
                        <MobileMenu
                          menuOpciones={menuOpciones}
                          referentContent={referentContent}
                          active={active}
                          opciones={opciones}
                          logosHeader={logosHeader}
                          handleClick={handleClick}
                          handleDrawerClose={handleDrawerToggle}
                          isDark={isDark}
                          logoMagenta={logoMagenta}
                          logoBlack={logoBlack}
                          renderLoginButton={renderLoginButton}
                          gigyaContext={data.gigyaContext}
                          handleSearcher={handleSearcher}
                        />
                      </div>
                    </Box>
                  </Slide>
                </Modal>
                )}
              </>
            )}
          </div>
        )}
      </GlobalContext.Consumer>
      {isSearcherActive && <Searcher type="empleo" onClose={handleCloseSearcher} />}
    </div>
  );
};

export default Header;
